/**
 * API utility for Azure Static Web Apps with authentication
 * Handles authenticated requests and proper error handling
 */

const LOCAL_BYPASS_STORAGE_KEY = 'BYPASS_SSO';
const AUTH_DIAGNOSTIC_STORAGE_KEY = 'SWA_AUTH_DIAGNOSTIC';

const buildLocalDevClientPrincipal = () => ({
  identityProvider: 'local-dev',
  userId: 'local-dev-user',
  userDetails: 'joshua@newberrynetworks.com',
  userRoles: ['authenticated', 'admin'],
  claims: []
});

const isLocalDevelopmentHost = (hostname) => (
  hostname === 'localhost' ||
  hostname === '127.0.0.1' ||
  hostname.includes('localhost') ||
  hostname === '192.168.1.238' ||
  hostname.startsWith('192.168.') ||
  hostname.startsWith('10.') ||
  hostname.startsWith('172.')
);

const shouldUseLocalApiAuthBypass = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname || '';
  const bypassAuth = localStorage.getItem(LOCAL_BYPASS_STORAGE_KEY) === 'true'
    || window.location.search.includes('bypass_sso=true');
  const isIpAddress = /^\d+\.\d+\.\d+\.\d+$/.test(hostname);

  return isLocalDevelopmentHost(hostname) && (bypassAuth || isIpAddress);
};

const readSessionStorageJson = (key) => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.warn(`Failed to parse session storage key ${key}:`, error);
    return null;
  }
};

const writeSessionStorageJson = (key, value) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    console.warn(`Failed to persist session storage key ${key}:`, error);
  }
};

const clearSessionStorageKey = (key) => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(key);
  } catch (error) {
    console.warn(`Failed to clear session storage key ${key}:`, error);
  }
};

const buildAuthDiagnostic = (type, details = {}) => ({
  type,
  host: typeof window !== 'undefined' ? window.location.host : null,
  path: typeof window !== 'undefined' ? window.location.pathname : null,
  recordedAt: new Date().toISOString(),
  ...details
});

export const getAuthDiagnostic = () => readSessionStorageJson(AUTH_DIAGNOSTIC_STORAGE_KEY);

export const setAuthDiagnostic = (type, details = {}) => {
  const diagnostic = buildAuthDiagnostic(type, details);
  writeSessionStorageJson(AUTH_DIAGNOSTIC_STORAGE_KEY, diagnostic);
  return diagnostic;
};

export const clearAuthDiagnostic = () => {
  clearSessionStorageKey(AUTH_DIAGNOSTIC_STORAGE_KEY);
};

const buildRequestHeaders = (headers = {}) => {
  const requestHeaders = new Headers(headers);

  if (shouldUseLocalApiAuthBypass() && !requestHeaders.has('x-ms-client-principal')) {
    requestHeaders.set('x-ms-client-principal', btoa(JSON.stringify(buildLocalDevClientPrincipal())));
  }

  return requestHeaders;
};

export const authenticatedFetch = (input, options = {}) => {
  return fetch(input, {
    ...options,
    credentials: options.credentials || 'include',
    headers: buildRequestHeaders(options.headers)
  });
};

// Resolve API base URL for local, production, and explicit overrides
export const getApiBase = () => {
  try {
    const maybeWindow = typeof window !== 'undefined' ? window : undefined;
    // Prefer explicit runtime override first
    const runtimeOverride = maybeWindow && maybeWindow.__API_BASE_URL__;
    if (typeof runtimeOverride === 'string' && runtimeOverride.trim().length > 0) {
      return runtimeOverride.replace(/\/$/, '');
    }

    // Then prefer build-time env (supports Vite-style var when injected into process.env in CI)
    const viteEnv = (typeof process !== 'undefined' ? process.env?.VITE_API_BASE_URL : undefined);
    if (typeof viteEnv === 'string' && viteEnv.trim().length > 0) {
      return viteEnv.replace(/\/$/, '');
    }

    // Default to the Vite/SWA `/api` path so local development can use the configured dev proxy.
    return '/api';
  } catch (_) {
    // Safe final default
    return '/api';
  }
};

const API_BASE_URL = getApiBase();

const buildLoginUrl = (redirectUri) => {
  const target = redirectUri || (typeof window !== 'undefined' ? window.location.href : '/');
  return `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(target)}`;
};

export const redirectToLogin = (redirectUri) => {
  if (typeof window === 'undefined') {
    return;
  }

  window.location.href = buildLoginUrl(redirectUri);
};

/**
 * Make an authenticated API request
 * Azure Static Web Apps automatically includes authentication cookies
 * but we need to ensure credentials are included
 */
export const apiRequest = async (endpoint, options = {}) => {
  const base = API_BASE_URL || getApiBase();
  const url = endpoint.startsWith('/') ? `${base}${endpoint}` : `${base}/${endpoint}`;
  
  const defaultOptions = {
    credentials: 'include', // Include authentication cookies for both local cross-origin and hosted same-origin calls
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  };

  const requestOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  try {
    console.log(`🌐 API Request: ${requestOptions.method || 'GET'} ${url}`);
    
    const response = await authenticatedFetch(url, requestOptions);
    
    console.log(`📡 API Response: ${response.status} ${response.statusText}`);
    
    // Handle authentication redirects
    if (response.status === 401) {
      const authDiagnostic = setAuthDiagnostic('api_401_redirect', {
        endpoint: url,
        method: requestOptions.method || 'GET',
        status: response.status
      });
      console.warn('SWA auth diagnostic:', authDiagnostic);
      console.warn('🔒 Authentication required, redirecting to login...');
      redirectToLogin(window.location.href);
      return null;
    }
    
    // Handle other HTTP errors
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ API Error: ${response.status} ${response.statusText}`, errorText);
      
      // Try to parse as JSON first, fallback to text
      let errorMessage;
      try {
        const errorData = JSON.parse(errorText);
        errorMessage = errorData.error || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
      } catch {
        errorMessage = errorText || `HTTP ${response.status}: ${response.statusText}`;
      }
      
      throw new Error(errorMessage);
    }
    
    // Parse JSON response
    const contentType = response.headers.get('content-type');
    if (contentType && contentType.includes('application/json')) {
      const data = await response.json();
      console.log('✅ API Success:', data);
      return data;
    }

    const text = await response.text();
    console.log('✅ API Success (text):', text);
    return text;
  } catch (error) {
    console.error('💥 API Request Failed:', error);
    throw error;
  }
};

/**
 * Convenience methods for common HTTP methods
 */
export const api = {
  get: (endpoint, options = {}) => apiRequest(endpoint, { ...options, method: 'GET' }),
  post: (endpoint, data, options = {}) => apiRequest(endpoint, { 
    ...options, 
    method: 'POST', 
    body: data ? JSON.stringify(data) : undefined 
  }),
  put: (endpoint, data, options = {}) => apiRequest(endpoint, { 
    ...options, 
    method: 'PUT', 
    body: data ? JSON.stringify(data) : undefined 
  }),
  delete: (endpoint, options = {}) => apiRequest(endpoint, { ...options, method: 'DELETE' }),
};

/**
 * Check if user is authenticated
 * Azure Static Web Apps provides user info at /.auth/me
 * For local development, we can bypass SSO
 */
export const checkAuthStatus = async () => {
  // Check if we're in local development mode
  const isLocalDev = isLocalDevelopmentHost(window.location.hostname || '');
  
  // Check for bypass environment variable or local storage flag
  const bypassAuth = localStorage.getItem(LOCAL_BYPASS_STORAGE_KEY) === 'true' || 
                    window.location.search.includes('bypass_sso=true');
  
  // Auto-enable bypass for IP addresses since /.auth/me doesn't exist in local dev
  const isIpAddress = /^\d+\.\d+\.\d+\.\d+$/.test(window.location.hostname);
  
  if (isLocalDev && (bypassAuth || isIpAddress)) {
    console.log('🚀 Local Development: Bypassing SSO authentication');
    if (isIpAddress) {
      console.log('🌐 IP Address detected - auto-enabling auth bypass');
    }
    // Return mock authenticated user for local development
    return {
      clientPrincipal: buildLocalDevClientPrincipal()
    };
  }
  
  try {
    console.log('Checking SWA auth session on host:', window.location.host);
    const response = await fetch('/.auth/me', { credentials: 'same-origin' });

    if (!response.ok) {
      const authDiagnostic = setAuthDiagnostic('auth_me_http_error', {
        status: response.status,
        statusText: response.statusText
      });
      console.warn('SWA auth diagnostic:', authDiagnostic);
      return null;
    }

    const contentType = response.headers.get('content-type') || '';
    if (!contentType.includes('application/json')) {
      if (isLocalDev) {
        console.log('Local Development: auth endpoint returned non-JSON content; treating auth as unavailable.');
      }
      const authDiagnostic = setAuthDiagnostic('auth_me_non_json', {
        status: response.status,
        contentType
      });
      console.warn('SWA auth diagnostic:', authDiagnostic);
      return null;
    }

    const authInfo = await response.json();
    console.log('🔐 Auth Status:', authInfo);
    if (authInfo?.clientPrincipal) {
      clearAuthDiagnostic();
    } else {
      const authDiagnostic = setAuthDiagnostic('missing_client_principal', {
        status: response.status
      });
      console.warn('SWA auth diagnostic:', authDiagnostic);
    }
    return authInfo;
  } catch (error) {
    console.error('❌ Auth check failed:', error);
    const authDiagnostic = setAuthDiagnostic('auth_me_exception', {
      message: error.message
    });
    console.warn('SWA auth diagnostic:', authDiagnostic);
    
    // If we're in local dev and auth check fails, offer bypass option
    if (isLocalDev) {
      console.log('💡 Local Development: SSO failed. Use ?bypass_sso=true or localStorage.setItem("BYPASS_SSO", "true") to bypass.');
      console.log('🌐 Network Access: This also works for local IP addresses (192.168.x.x, 10.x.x.x, etc.)');
    }
    
    return null;
  }
};

/**
 * Get current user information
 */
export const getCurrentUser = async () => {
  const authInfo = await checkAuthStatus();
  return authInfo?.clientPrincipal || null;
};

/**
 * Enable SSO bypass for local development
 */
export const enableLocalAuthBypass = () => {
  localStorage.setItem(LOCAL_BYPASS_STORAGE_KEY, 'true');
  console.log('✅ SSO bypass enabled for local development. Refresh the page.');
};

/**
 * Disable SSO bypass
 */
export const disableLocalAuthBypass = () => {
  localStorage.removeItem(LOCAL_BYPASS_STORAGE_KEY);
  console.log('✅ SSO bypass disabled. Refresh the page.');
};

/**
 * Logout user
 */
export const logout = () => {
  window.location.href = '/.auth/logout';
};

/**
 * Fix broken images on eBay listing
 * @param {string} ebayItemId - The eBay item ID to fix
 * @returns {Promise<Object>} - Result of the fix operation
 */
export const fixEbayListingImages = async (ebayItemId) => {
  return apiRequest(`/fix-ebay-listing-images?ebayItemId=${ebayItemId}`, {
    method: 'POST'
  });
};

/**
 * Batch fix broken images on all eBay listings
 * @returns {Promise<Object>} - Results of batch fix operation
 */
export const batchFixEbayListingImages = async () => {
  return apiRequest('/fix-ebay-listing-images?batchMode=true', {
    method: 'POST'
  });
};

export default api;
